import apiClient from './client';
import type { PaginatedResponse } from '@/types/api';
import type {
  Group,
  GroupMember,
  GroupMemberRole,
  GroupFormData,
  GroupFilters,
  AddGroupMemberData,
  GroupSection,
} from '@/types/group';

export async function getGroups(
  params?: GroupFilters,
): Promise<PaginatedResponse<Group>> {
  const response = await apiClient.get<PaginatedResponse<Group>>(
    '/groups/',
    { params },
  );
  return response.data;
}

export async function getGroup(id: string): Promise<Group> {
  const response = await apiClient.get<{ data: Group }>(`/groups/${id}/`);
  return response.data.data;
}

export async function createGroup(data: GroupFormData): Promise<Group> {
  const response = await apiClient.post<{ data: Group }>('/groups/', data);
  return response.data.data;
}

export async function updateGroup(
  id: string,
  data: Partial<GroupFormData>,
): Promise<Group> {
  const response = await apiClient.put<{ data: Group }>(`/groups/${id}/`, data);
  return response.data.data;
}

export async function deleteGroup(id: string): Promise<void> {
  await apiClient.delete(`/groups/${id}/`);
}

export async function getGroupMembers(
  id: string,
): Promise<GroupMember[]> {
  const response = await apiClient.get<{ data: GroupMember[] }>(
    `/groups/${id}/members/`,
  );
  return response.data.data;
}

export async function addGroupMember(
  id: string,
  data: AddGroupMemberData,
): Promise<void> {
  await apiClient.post(`/groups/${id}/members/`, data);
}

export async function removeGroupMember(
  groupId: string,
  memberId: string,
): Promise<void> {
  await apiClient.delete(`/groups/${groupId}/members/${memberId}/`);
}

export async function updateGroupMemberRole(
  groupId: string,
  memberId: string,
  role: GroupMemberRole,
): Promise<void> {
  await apiClient.put(`/groups/${groupId}/members/${memberId}/`, { role });
}

export async function addGroupMembersBatch(
  groupId: string,
  members: Array<{ member_id: string; role?: string; section_id?: string | null }>,
): Promise<void> {
  await apiClient.post(`/groups/${groupId}/members/batch/`, { members });
}

export async function updateGroupMemberSection(
  groupId: string,
  memberId: string,
  sectionId: string | null,
): Promise<void> {
  await apiClient.put(`/groups/${groupId}/members/${memberId}/section/`, {
    section_id: sectionId,
  });
}

// ── Sections ──────────────────────────────────────────────────────────

export async function createGroupSection(
  groupId: string,
  data: { name: string; color?: string | null },
): Promise<GroupSection> {
  const response = await apiClient.post<{ data: GroupSection }>(
    `/groups/${groupId}/sections/`,
    data,
  );
  return response.data.data;
}

export async function updateGroupSection(
  sectionId: string,
  data: { name?: string; color?: string | null },
): Promise<GroupSection> {
  const response = await apiClient.put<{ data: GroupSection }>(
    `/sections/${sectionId}/`,
    data,
  );
  return response.data.data;
}

export async function deleteGroupSection(sectionId: string): Promise<void> {
  await apiClient.delete(`/sections/${sectionId}/`);
}

export async function reorderGroupSections(
  groupId: string,
  sectionIds: string[],
): Promise<GroupSection[]> {
  const response = await apiClient.put<{ data: GroupSection[] }>(
    `/groups/${groupId}/sections/reorder/`,
    { section_ids: sectionIds },
  );
  return response.data.data;
}

export async function exportGroupMembers(groupId: string): Promise<Blob> {
  const response = await apiClient.get(`/groups/${groupId}/export/`, {
    responseType: 'blob',
  });
  return response.data as Blob;
}
